import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { makeExistingTaskDescChanged, setExpanded, toggleDescPopup } from "../../../../actions/taskSidebar";
import { setShowFormPopup } from "../../../../actions/taskSidebar";
import { icon, label, number } from "../../../../config/constants";
import KendoEditor from "../../../../editor/KendoEditor";
import { editorToolType } from "../../../../editor/editor.Constant";
import { handleTaskKeyUpdate } from "../../../Tasks/tasks.service";
import { isFieldDefined } from "../../sidebar.common";
import { DESC_TYPE } from "../../sidebar.constants";
import "./description.scss";

/**
 * Common Component for approved task & normal task description
 * Parent component => TaskApproval, ApprovalDescription
 * @param {Object} props
 * @author Himanshu Negi
 * @returns JSX
 */
const Descriptions = (props) => {
  const {
    desc,
    setDesc,
    taskKey,
    type,
    viewRef,
    autoSaveKey,
    setUpdateAutoSaveKey,
    editableRef,
    editorRef,
    editorEditIcon,
    isEditable,
    setIsEditable,
    provider,
    setProvider,
  } = props;
  const { task, isNewTask, expanded, showDescription, newTaskData, isYdocexists, showDescPopup, tempYdocRoom } = useSelector(
    (state) => state.taskSidebar
  );
  const { workflowFeatures } = useSelector((state) => state?.taskWorkflowDetails);
  const { id: userId } = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const defaultDesc = "<p> </p>";
  /**
   * Use useMemo to create the collab object for real-time editing
   * @author Abhishek Dhillon
   */
  const collab = useMemo(() => {
    if (task && type === DESC_TYPE.DESCRIPTION) {
      // in case of approval description, we are not using collab
      return {
        value: true,
        isYdocExists: true,
        YdocRoom: isNewTask ? String(tempYdocRoom) : String(task.taskId),
      };
    }
    return null;
  }, [isNewTask, isYdocexists, task?.taskId, userId]);

  /**
   * Initialize description when task changes or workflow features update
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    initializeDescriptionOnTaskChange();
  }, [showDescription, task?.Description, newTaskData?.description, showDescPopup?.type, workflowFeatures, isYdocexists, task?.ApprovalDescription]);

  /**
   * Set appropriate description based on task type and state
   * @author Abhishek Dhillon
   */
  const initializeDescriptionOnTaskChange = () => {
    if (task?.ApprovalDescription && taskKey === "approvalDescription") {
      setDesc(task.ApprovalDescription);
      return;
    } else {
      if ((isNewTask || !isYdocexists) && (task?.Description || newTaskData?.description)) {
        const features = Array?.isArray(workflowFeatures) ? workflowFeatures : [];
        const featureFive = features?.find((f) => f?.Id === number.FIVE);
        if (isNewTask && showDescription && featureFive?.IsSelected === number.ONE) {
          setDesc(getSafeDescription(newTaskData?.description));
        } else if (isNewTask) {
          setDesc(getSafeDescription(newTaskData?.description));
        } else if (!isNewTask && task?.Description) {
          setDesc(getSafeDescription(task.Description));
        }
      }
    }
  };

  /**
   * Return default description if data is null/undefined
   * @author Abhishek Dhillon
   */
  const getSafeDescription = (data) => data || defaultDesc;

  /**
   * Updates description when auto save key gets updated
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    autoSaveKey > number.ONE && updateDescription(desc);
  }, [autoSaveKey]);

  /**
   * Updates description and removes empty paragraphs if there is no content present in the description box
   * @author Abhishek Dhillon
   */
  const updateDescription = async (nextValue) => {
    if (nextValue) {
      handleTaskKeyUpdate(isNewTask, taskKey, nextValue);
    }
    // if (type === DESC_TYPE.DESCRIPTION && isNewTask && nextValue !== newTaskData?.description) {
    //   handleTaskKeyUpdate(true, "isDescChanged", true);
    // }
    !isNewTask && type === DESC_TYPE.DESCRIPTION && dispatch(makeExistingTaskDescChanged());
  };

  /**
   * It open description popup and closes the description section on TaskSidebar
   * @param {Void}
   * @returns {Void}
   * @author Himanshu Negi
   */
  const handleDescPopup = async (type) => {
    const newExpand =
      type === DESC_TYPE.DESCRIPTION
        ? expanded.filter((item) => item !== "task-description")
        : expanded.filter((item) => item !== "task-approval-description");
    dispatch(toggleDescPopup({ status: true, type }));
    dispatch(setExpanded(newExpand));
    dispatch(setShowFormPopup(false));
  };

  /**
   * Toggle Editable state and update description if editable
   * @param None
   * @return None
   * @author Shivam Mishra
   */
  const toggleEditable = async () => {
    if (isEditable) {
      updateDescription(desc);
    }
    setIsEditable(!isEditable);
    editableRef.current = !isEditable;
  };

  /**
   * Determines if the editor should be displayed
   * @returns {boolean}
   * @author Abhishek Dhillon
   */
  const shouldDisplayEditor = () => {
    return (isNewTask && desc) || !isNewTask;
  };

  return (
    <React.Fragment>
      <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
        <button
          onClick={() => handleDescPopup(type)}
          className={`description-popup-icon position-absolute bg-transparent d-inline-flex tour-description-popup-icon ${
            type === DESC_TYPE.APPROVAL_DESCRIPTION ? "approved-text" : "desc-popout-text"
          }`}
          title={label.POP_OUT}
          data-testid='description-popup-icon'>
          {icon.OPEN_IN_FULL}
        </button>
      </Tooltip>
      <div className='task-description-editor desc-popup-editor task-desc-editor' id='sidebar-desc'>
        {shouldDisplayEditor() && (
          <KendoEditor
            desc={desc}
            setDesc={setDesc}
            viewRef={viewRef}
            editableRef={editableRef}
            toggleEditable={toggleEditable}
            editorRef={editorRef}
            editorEditIcon={editorEditIcon}
            autoSaveKey={autoSaveKey}
            setUpdateAutoSaveKey={setUpdateAutoSaveKey}
            toolbar={editorToolType.TASK_SIDEBAR_TOOL}
            collab={type === DESC_TYPE.DESCRIPTION ? collab : { value: false }}
            provider={provider}
            setProvider={setProvider}
            taskKey={taskKey}
            showEditIcons={false}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default Descriptions;
